/**
 * Label Families Panel — classify a sheet by confirming shape families
 *
 * Clusters the active layout's labels into shape families (all string
 * numbers, all DCCB headings, title-block noise…) and offers one action per
 * family: classify a pure-number family as strings with a range-bounded
 * pattern, draft a schema from a structured family's representative sample,
 * or ignore a noise family. One confirmation classifies thousands of members.
 *
 * "Apply to all layouts" copies the active layout's schemas + patterns to
 * every sheet of the site and re-classifies them — multi-sheet sites from
 * one drawing office share a label grammar.
 */

import { useMemo, useCallback } from 'react';
import { useProjectStore } from '../../stores/projectStore';
import { useUIStore } from '../../stores/uiStore';
import {
  clusterFamilies,
  proposeForFamily,
  type LabelFamily,
  type FamilyProposal,
} from '../../domain/layout/families';
import { compileSchemas } from '../../domain/layout/labelSchemaCompiler';
import { reclassifyAndBind } from '../../domain/layout/parentBind';
import type { ClassificationPattern, LabelSchema } from '../../domain/layout/types';
import './LabelFamiliesPanel.css';

const LEVEL_COLORS: Record<string, string> = {
  station: '#ef4444',
  inverter: '#3b82f6',
  mppt: '#06b6d4',
  dccb: '#f97316',
  string: '#22c55e',
  module: '#a855f7',
  row: '#ec4899',
  zone: '#14b8a6',
  infrastructure: '#6b7280',
  unclassified: '#9ca3af',
};

const MAX_CARDS = 12;
const MIN_FAMILY_SIZE = 3;

export default function LabelFamiliesPanel() {
  const siteLayout = useProjectStore((s) => s.data.siteLayout);
  const siteLayouts = useProjectStore((s) => s.data.siteLayouts);
  const setLabelSchemas = useProjectStore((s) => s.setLabelSchemas);
  const updateConfig = useProjectStore((s) => s.updateSiteLayoutConfig);
  const updateEntities = useProjectStore((s) => s.updateSiteLayoutEntities);
  const setIgnoredFamilies = useProjectStore((s) => s.setIgnoredFamilies);
  const applyToAllLayouts = useProjectStore((s) => s.applyClassificationToAllLayouts);
  const showConfirm = useUIStore((s) => s.showConfirm);
  const addNotification = useUIStore((s) => s.addNotification);

  const entities = siteLayout?.textEntities;
  const ignored = siteLayout?.ignoredFamilies ?? [];

  const families = useMemo(() => {
    if (!entities?.length) return [];
    return clusterFamilies(entities);
  }, [entities]);

  // Split actionable families (classify / draft-schema proposals) from
  // noise families whose only affordance would be Ignore — the latter are
  // collapsed into ONE summary row (owner feedback 2026-09-02: a card whose
  // only button is Ignore wastes a card, and the list forced scrolling).
  const { actionable, noise } = useMemo(() => {
    const candidates = families.filter(
      (f) =>
        f.unclassified > 0 &&
        f.count >= MIN_FAMILY_SIZE &&
        !ignored.includes(f.signature),
    );
    const act: Array<{ family: LabelFamily; proposal: FamilyProposal }> = [];
    const noi: LabelFamily[] = [];
    for (const family of candidates) {
      const proposal = proposeForFamily(family);
      if (proposal.kind === 'ignore') noi.push(family);
      else act.push({ family, proposal });
    }
    return { actionable: act.slice(0, MAX_CARDS), noise: noi };
  }, [families, ignored]);

  const noiseLabelCount = useMemo(
    () => noise.reduce((s, f) => s + f.unclassified, 0),
    [noise],
  );

  const handleIgnoreAllNoise = useCallback(() => {
    setIgnoredFamilies([...ignored, ...noise.map((f) => f.signature)]);
  }, [ignored, noise, setIgnoredFamilies]);

  /** Re-classify the active layout with a given config and persist. */
  const reclassifyWith = useCallback(
    (config: ClassificationPattern[]) => {
      if (!siteLayout) return;
      const reclassified = reclassifyAndBind(siteLayout.textEntities, config);
      updateConfig(config);
      updateEntities(reclassified, { reclassified: true });
    },
    [siteLayout, updateConfig, updateEntities],
  );

  const handlePattern = useCallback(
    (family: LabelFamily, proposal: Extract<FamilyProposal, { kind: 'pattern' }>) => {
      if (!siteLayout) return;
      const existing = siteLayout.classificationConfig ?? [];
      const id = `family-${family.signature.replace(/[^A-Za-z0-9#]/g, '_')}`;
      if (existing.some((p) => p.id === id)) return;
      const pattern: ClassificationPattern = {
        id,
        regex: proposal.regex,
        level: proposal.level,
        captureGroups: proposal.captureGroups,
        priority: existing.length + 1,
        builtin: false,
      };
      reclassifyWith([...existing, pattern]);
      addNotification({
        type: 'success',
        title: 'Family classified',
        message: `${family.count} labels matching ${family.signature} → ${proposal.level}`,
      });
    },
    [siteLayout, reclassifyWith, addNotification],
  );

  const handleSchema = useCallback(
    (proposal: Extract<FamilyProposal, { kind: 'schema' }>) => {
      if (!siteLayout) return;
      // Append the drafted schema to the persisted set and re-apply all
      // schemas (same merge semantics as the Label Schema panel's Apply All).
      const schemas: LabelSchema[] = [...(siteLayout.labelSchemas ?? []), proposal.schema];
      const compiled = compileSchemas(schemas.filter((s) => s.sampleLabel && s.segments.length > 0));
      const nonSchema = (siteLayout.classificationConfig ?? []).filter(
        (p) => !p.id.startsWith('schema-auto-'),
      );
      setLabelSchemas(schemas);
      reclassifyWith([...compiled, ...nonSchema]);
      addNotification({
        type: 'success',
        title: 'Schema drafted',
        message: `Schema from "${proposal.schema.sampleLabel}" applied — refine segments in Label Schema below`,
      });
    },
    [siteLayout, setLabelSchemas, reclassifyWith, addNotification],
  );

  const handleIgnore = useCallback(
    (family: LabelFamily) => {
      setIgnoredFamilies([...ignored, family.signature]);
    },
    [ignored, setIgnoredFamilies],
  );

  const handleRestoreIgnored = useCallback(() => {
    setIgnoredFamilies([]);
  }, [setIgnoredFamilies]);

  const layoutCount = siteLayouts?.length ?? (siteLayout ? 1 : 0);

  const handleApplyAll = useCallback(() => {
    showConfirm({
      title: 'Apply to all layouts',
      message:
        `Copy this layout's schemas and patterns to all ${layoutCount} layouts ` +
        'of this site and re-classify their labels? Manual label overrides are kept.',
      confirmLabel: 'Apply to all',
      onConfirm: () => {
        applyToAllLayouts();
        addNotification({
          type: 'success',
          title: 'Applied to all layouts',
          message: `${layoutCount} layouts re-classified with this layout's schemas & patterns`,
        });
      },
    });
  }, [showConfirm, layoutCount, applyToAllLayouts, addNotification]);

  if (!siteLayout) return null;

  return (
    <div className="lfp">
      {actionable.length === 0 && noise.length === 0 ? (
        <p className="lfp__empty">
          No unclassified families — every repeated label shape on this sheet is
          classified or ignored.
        </p>
      ) : (
        <div className="lfp__list">
          {actionable.map(({ family, proposal }) => (
            <div key={family.signature} className="lfp__card">
              <div className="lfp__head">
                <code className="lfp__sig">{family.signature}</code>
                <span className="lfp__count">
                  {family.unclassified === family.count
                    ? `${family.count}`
                    : `${family.unclassified}/${family.count}`}
                </span>
              </div>
              <div
                className="lfp__samples-line"
                title={family.samples.join('  ·  ')}
              >
                {family.samples.slice(0, 3).join(' · ')}
                {family.numericRange &&
                  `  (${family.numericRange.min}–${family.numericRange.max})`}
              </div>
              <div className="lfp__actions">
                {proposal.kind !== 'ignore' && (
                  <button
                    type="button"
                    className="lfp__btn lfp__btn--primary"
                    style={{
                      borderColor:
                        proposal.kind === 'pattern'
                          ? LEVEL_COLORS[proposal.level]
                          : LEVEL_COLORS[proposal.schema.level] ?? undefined,
                    }}
                    onClick={() =>
                      proposal.kind === 'pattern'
                        ? handlePattern(family, proposal)
                        : handleSchema(proposal)
                    }
                  >
                    {/* Say the proposed level outright — the border color
                        alone was an unlabeled riddle (owner, 2026-09-02) */}
                    {proposal.kind === 'schema'
                      ? `${proposal.actionLabel} → ${proposal.schema.level.charAt(0).toUpperCase()}${proposal.schema.level.slice(1)}`
                      : proposal.actionLabel}
                  </button>
                )}
                <button
                  type="button"
                  className="lfp__btn"
                  onClick={() => handleIgnore(family)}
                >
                  Ignore
                </button>
              </div>
            </div>
          ))}

          {noise.length > 0 && (
            <div className="lfp__noise">
              <div className="lfp__noise-row">
                <span className="lfp__noise-text">
                  {noise.length} noise famil{noise.length === 1 ? 'y' : 'ies'} ·{' '}
                  {noiseLabelCount} labels
                </span>
                <button type="button" className="lfp__btn" onClick={handleIgnoreAllNoise}>
                  Ignore all
                </button>
              </div>
              <details className="lfp__noise-details">
                <summary>show</summary>
                {noise.map((f) => (
                  <div key={f.signature} className="lfp__noise-item" title={f.samples.join('  ·  ')}>
                    <code className="lfp__sig">{f.signature}</code>
                    <span className="lfp__count">{f.unclassified}</span>
                    <span className="lfp__noise-samples">{f.samples.slice(0, 2).join(' · ')}</span>
                  </div>
                ))}
              </details>
            </div>
          )}
        </div>
      )}

      <div className="lfp__footer">
        {ignored.length > 0 && (
          <button type="button" className="lfp__link" onClick={handleRestoreIgnored}>
            Restore {ignored.length} ignored famil{ignored.length === 1 ? 'y' : 'ies'}
          </button>
        )}
        {layoutCount > 1 && (
          <button type="button" className="lfp__btn lfp__btn--all" onClick={handleApplyAll}>
            Apply to all {layoutCount} layouts
          </button>
        )}
      </div>
    </div>
  );
}
